import {
    Component, Injector, OnInit, ViewContainerRef, ViewEncapsulation, ViewChild, NgZone, OnDestroy,
    HostListener, AfterViewChecked,
    AfterViewInit, ElementRef
} from '@angular/core';
import { Event, Router } from '@angular/router';
import { AppConsts } from '@shared/commonHelper/AppConsts';
import { SubscriptionStartType } from '@shared/commonHelper/AppEnums';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { DayRolloverService } from '@shared/common/day-rollover/day-rollover.service';
import { CalendarFeatureService } from '@shared/common/calendar-feature/calendar-feature.service';
import { UrlHelper } from '@shared/helpers/UrlHelper';
import { FavoriteService } from '@shared/utils/services/favorite.service';
import * as moment from 'moment';
import { AppComponentBase } from 'shared/common/app-component-base';
import { UserSignalrService } from './shared/layout/change-status/user-signalr.service';
import { DataService } from './shared/common/auth/data.service';
import { EventFormService } from './shared/common/calendar/event-form.service';
import {
    CompanyServiceProxy, ContactServiceProxy, FavoriteOrderInput, FavoriteServiceProxy, FolderType,
    NotificationServiceProxy, ReorderFavoritesInput,
    SetNotificationReadInput, TaskServiceProxy, UserServiceProxy, AgendaConsolidatedServiceProxy,
    MyCalendarSettingsServiceProxy
} from '@shared/service-proxies/service-proxies';
import { DragulaService } from 'ng2-dragula';
import { Observable, Subject, Subscription, interval, merge } from 'rxjs';
import { AppAuthService } from './shared/common/auth/app-auth.service';
import { CONVERSATIONS_PERMISSION, ConversationsOverlayService } from './shared/common/conversations/conversations-overlay.service';
import { ConversationCountsStore } from './shared/common/conversations/conversation-counts.store';
import { ConversationBadgeVm } from './shared/common/conversations/conversation-badge';
import { ConnectionService } from 'ng-connection-service';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { app } from '@microsoft/teams-js';
import { openSupportEmail } from '@app/shared/common/support/contact-support';
import { ProfileModalComponent } from './main/profile-modal/profile-modal.component';
import { ConnectedAppsModalComponent } from './main/connected-apps-modal/connected-apps-modal.component';
import { cloneDeep } from 'lodash';
import { ServiceProxy as StripeServiceProxy } from '@shared/service-proxies/stripe-service-proxy';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, filter, map, takeUntil } from '@node_modules/rxjs/operators';
import { TimeTrackingService } from './main/time-tracking/services/time-tracking.service';
import { GetHelpStateService } from './shared/common/get-help/get-help-state.service';
import { GetHelpFlight } from './shared/common/get-help/get-help.models';

/** The flyer's position, with its travel as ready-made CSS lengths for the --dx / --dy custom properties. */
interface GetHelpFlyer {
    left: number;
    top: number;
    dx: string;
    dy: string;
}

/**
 * The routes that fill the window without the app shell (no sidebar, no billing banner, no "You're in" modal): the
 * Adaptive Onboarding at /app/onboarding (plan §3.2, OB-G-12) and the old welcome until D-10 retires it.
 */
const FULL_SCREEN_ROUTES: readonly string[] = ['/app/onboarding', '/app/main/welcome'];

/** Whether a router URL is a full-screen route: its path (query string and fragment ignored) is one of them or below one. */
export function isFullScreenUrl(url: string | null | undefined): boolean {
    const path: string = (url || '').split(/[?#]/)[0];

    return FULL_SCREEN_ROUTES.some((route: string) => path === route || path.startsWith(route + '/'));
}

/** Settings tab 9, "Intelligence" (plan §7.3, D18); settings-nav whitelists it. */
const INTELLIGENCE_SETTINGS_TAB = 9;

@Component({
    templateUrl: './app.component.html',
    encapsulation: ViewEncapsulation.None
})
export class AppComponent extends AppComponentBase implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {
    isMenuCollapsed = false;
    showCreateDropdown = false;
    /** Drives the "New version ready" banner in the sidebar. */
    updateAvailable = false;
    todayMeetingsCount = 0;
    todayTaskCount = 0;
    /** E8/E2: show the "Calendar" sidebar item when the workspace feature is on. Live via
     *  CalendarFeatureService — seeded from getMySettings, refreshed by the tenant.calendar.featureToggled
     *  SignalR broadcast — so the item appears/disappears in real time without a refresh. */
    get showCalendarNav(): boolean {
        return this._calendarFeature.enabled;
    }
    /** Lead Pipeline: the "Pipeline" sidebar item (handoff (12)) exists only with the flag on. */
    readonly showPipelineNav = AppConsts.leadPipelineFeatureEnabled;
    mainNavItems: { label: string; icon: string; route: string; badge?: number }[] = [
        { label: 'My day', icon: 'sun', route: '/app/main/my-day' },
        { label: 'Mail', icon: 'mail', route: '/app/main/emails' },
        { label: 'Meetings', icon: 'video', route: '/app/main/meetings' },
        { label: 'Projects', icon: 'briefcase', route: '/app/main/projects' },
        { label: 'Clients', icon: 'users', route: '/app/main/clients' },
    ];
    workNavItems: { label: string; icon: string; route: string; badge?: number }[] = [
        { label: 'Search', icon: 'search', route: '' },
        { label: 'Comments', icon: 'message-square', route: '' },
        { label: 'Resources', icon: 'link', route: '' },
    ];

    toggleMenu() {
        this.isMenuCollapsed = !this.isMenuCollapsed;
    }

    isNavActive(route: string): boolean {
        if (!route) return false;
        if (this.activeMenuOverride) {
            return this.activeMenuOverride === route;
        }
        return this.url.startsWith(route);
    }

    navigateMenu(route: string) {
        if (!route) return;
        window.localStorage.setItem('sliderState', '[]');
        this.dataService.closeMainMenuItems.next();
        this.dataService.closeSubMenuItems.next();
        this.dataService.closeTertiaryItems.next();
        this.router.navigateByUrl(route);
    }

    toggleCreateDropdown() {
        this.showCreateDropdown = !this.showCreateDropdown;
    }

    openCalendar() {
        let isIframe = window !== window.parent && !window.opener;
        if (isIframe) {
            app.openLink('https://teams.microsoft.com/_#/calendarv2');
        } else {
            window.open('https://outlook.office.com/calendar/', '_blank');
        }
    }

    closeCreateDropdown() {
        this.showCreateDropdown = false;
    }

    createAction(type: string) {
        this.showCreateDropdown = false;
        switch (type) {
            case 'task': this.addTask(); break;
            case 'event': this.addEvent(); break;
            case 'project': this.addProject(); break;
            case 'person': this.addContact(); break;
            case 'company': this.addCompany(); break;
        }
    }

    // @ViewChild('vfHelpModal') vfHelpModal: HelpComponent;
    @ViewChild('upgradeSubscriptionSuccessModal') successUpgrade: ModalDirective;
    @ViewChild('profileModal') profileModal: ProfileModalComponent;
    @ViewChild('connectedAppsModal') connectedAppsModal: ConnectedAppsModalComponent;
    @ViewChild('quickAddPopover', { static: false }) quickAddPopover: NgbPopover;
    @ViewChild('contactPopover', { static: false }) contactPopover: NgbPopover;
    @ViewChild('extraActionsPopover', { static: false }) extraActionsPopover: NgbPopover;
    @ViewChild('helpActionsPopover', { static: false }) helpActionsPopover: NgbPopover;
    @ViewChild('profileOptionsPopover', { static: false }) profileOptionsPopover: NgbPopover;
    /** FU-Z4-FIX FZ-3: the profile button, which takes the focus before Connected apps opens (the drawer gives it back on close). */
    @ViewChild('profileOptionsButton', { static: false }) profileOptionsButton: ElementRef<HTMLButtonElement>;
    /** The profile block is where the first-visit help banner's ? icon lands (Get help sits just above it). */
    @ViewChild('profileSection') set profileSection(el: ElementRef<HTMLElement> | undefined) {
        this.profileSectionEl = el ? el.nativeElement : null;
        this._getHelpState.registerMenuTarget(this.profileSectionEl);
    }
    private profileSectionEl: HTMLElement | null = null;
    /** The banner's ? icon in flight to Get help. Drawn by the shell so a menu navigation that unmounts My Day can't cut it short. */
    readonly getHelpFlight$: Observable<GetHelpFlyer | null> = this._getHelpState.flight$.pipe(
        map((f: GetHelpFlight | null) => (f ? { left: f.x, top: f.y, dx: f.dx + 'px', dy: f.dy + 'px' } : null))
    );
    /** Main-menu Get help: shown whenever My Day's first-visit banner is not on screen. */
    readonly getHelpMenuVisible$: Observable<boolean> = this._getHelpState.menuVisible$;
    /** The moment the banner's flight lands: the item plays the design's slot-in / pulse / shimmer / type-in. */
    readonly getHelpMenuLanding$: Observable<boolean> = this._getHelpState.menuLanding$;
    private viewContainerRef: ViewContainerRef;
    private router: Router;
    subscriptionStartType = SubscriptionStartType;
    currentUserTZ: moment.Moment = null;
    installationMode = true;
    subscription: Subscription;
    isConnected = true;
    selectedDate: moment.Moment;
    refrshBtn = false;
    newTaskCount = '';
    /** Global Conversations (slice 2, §6.2): the sidebar item replaces Comments; shown only with Pages.Agenda (set once). */
    readonly showConversationsNav: boolean;
    /** The server's badge view model; the async pipe inside the `showConversationsNav` gate is its only subscriber. */
    readonly conversationsBadge$: Observable<ConversationBadgeVm>;
    /** The overlay's open state, as its host reports it: drives the item's active state whatever opened it. */
    readonly conversationsOpen$: Observable<boolean>;
    needToRefreshToken = false;
    showNewVersion = false;
    newTaskData: Array<any> = new Array<any>();
    isInTrialPeriod = false;
    subscriptionDaysLeftCount = 0;
    subscriptionDaysLeftLabel = 'days';
    subscriptionDaysLimit = 7;
    subs = new Subscription();
    url = '';
    /**
     * True on a full-screen route (/app/onboarding, the old /app/main/welcome): the template hides the sidebar and the
     * shell overlays. Precomputed from the router events (isFullScreenUrl), so the template holds no route logic.
     */
    isFullScreenRoute = false;
    activeMenuOverride = '';
    startDateFilter = moment.parseZone(this.appSession.user.currentTimeAsUserTz).format('yyyy-MM-DD HH:mm');
    endDateFilter = moment.parseZone(this.appSession.user.currentTimeAsUserTz).format('yyyy-MM-DD HH:mm');
    selectedFilter = FolderType._1;
    isMenuClicked = false;
    isPlannerOpen = false;
    isCalOpen = false;
    isSaving = false;
    isAccLinked = true;
    interval: NodeJS.Timer;

    documentName = '';
    isAlertsActive = false;
    /** Billing admins (Pages.Settings: Owner and Admin) see Upgrade and "Billing and plan" in the profile menu. */
    canManageBilling = false;
    wind = window.innerWidth;
    mobileWidth = AppConsts.mobileScreenWidth;
    emailAutomationFeatureEnabled = AppConsts.emailAutomationFeatureEnabled;
    // Kill-switch for the Calendar & Events feature (E2); gates the Calendar shortcut in the profile menu.
    calendarV1FeatureEnabled = AppConsts.calendarV1FeatureEnabled;
    showPinOBModal = false;
    openedUnsavedDataLostModalFrom: string = '';
    addTaskDetails: any;
    isIframe = window !== window.parent && !window.opener;

    // Favorites preview (expanded sidebar) — one flat list, ordered by the user, no per-type sections.
    // How many the sidebar pins before "manage favorites" shows the rest. Mirrors FAV_N in the design.
    readonly FAV_SIDEBAR_LIMIT = 8;
    /** Dragula group for reordering the sidebar's favorites. Its own group, not the manager's. */
    private readonly FAV_DRAGULA = 'sidebarFavoritesDragula';
    favPreview: any[] = [];
    // True when the user has more favorites than the sidebar shows.
    hasHiddenFavorites = false;
    // Every favorite the user has, not just the ones on screen — the number on "See all {n}". Comes
    // from GetFavorites alongside the rows, so the cap does not hide it.
    favTotal = 0;
    isFavOrderLoading = false;
    showFavAddDropdown = false;
    showFavAddMenu = false;

    private taskUpdates$ = new Subject<void>();
    private pinnedProjectInfo$ = new Subject<void>();
    private cascadeApplied$ = new Subject<void>();
    private labelUpdates$ = new Subject<void>();
    private calendarNav$ = new Subject<void>();
    private appUpdate$ = new Subject<void>();
    /** Component-lifetime teardown (completed in ngOnDestroy). */
    private readonly destroy$ = new Subject<void>();
    public constructor(
        injector: Injector,
        private _getHelpState: GetHelpStateService,
        private _authService: AppAuthService,
        viewContainerRef: ViewContainerRef,
        private _router: Router,
        private dataService: DataService,
        private _eventForm: EventFormService,
        private _userSignalrService: UserSignalrService,
        private _appSessionService: AppSessionService,
        private _dayRollover: DayRolloverService,
        private connectionService: ConnectionService,
        private _taskService: TaskServiceProxy,
        private ngZone: NgZone,
        private swUpdate: SwUpdate,
        private _favoriteService: FavoriteServiceProxy,
        private _userService: UserServiceProxy,
        private _notificationService: NotificationServiceProxy,
        private _agendaConsolidatedService: AgendaConsolidatedServiceProxy,
        private _stripeServiceProxy: StripeServiceProxy,
        private _myCalProxy: MyCalendarSettingsServiceProxy,
        private _favorites: FavoriteService,
        private _calendarFeature: CalendarFeatureService,
        private dragulaService: DragulaService,
        private ttService: TimeTrackingService,
        private _conversationsOverlay: ConversationsOverlayService,
        private _conversationCounts: ConversationCountsStore) {
        super(injector);
        this.showConversationsNav = this.isGranted(CONVERSATIONS_PERMISSION);
        this.conversationsBadge$ = this._conversationCounts.badge$;
        this.conversationsOpen$ = this._conversationsOverlay.isOpen$;
        this.viewContainerRef = viewContainerRef; // You need this small hack in order to catch application root view container ref (required by ng2 bootstrap modal)
        this.router = _router;
        this.refrshBtn = false;
        this.connectionService.monitor().subscribe(isConnected => {
            this.isConnected = isConnected;
            if (this.isConnected) {
                this.refrshBtn = true;
                // SignalR initialization is handled in ngOnInit to avoid duplicate init
            } else {
                this.refrshBtn = false;
                $('.page-container-new').addClass('top-instruction');
            }
            if (this.isConnected && this.refrshBtn) {
                this.refrshBtn = false;
                $('.page-container-new').removeClass('top-instruction');
                location.reload();
            }
        });

        this.canManageBilling = this.permission.isGranted('Pages.Settings');
        if (this.appSession.tenant) {
            this.isInTrialPeriod = this.appSession.tenant.isInTrialPeriod;
            this.subscriptionDaysLeftCount = this.appSession.tenant.subscriptionDaysLeftCount;
            this.subscriptionDaysLeftLabel = this.subscriptionDaysLeftCount === 1 ? 'day' : 'days';
            // Custom Project Label (Labels & Terminology): seed the label store from the tenant's
            // saved wording so every surface renders the correct label from first paint.
            this.tenantLabelService.update(
                this.appSession.tenant.projectLabelSingular,
                this.appSession.tenant.projectLabelPlural
            );
        }
        // The first render already knows a full-screen route (no sidebar flash on /app/onboarding).
        this.isFullScreenRoute = isFullScreenUrl(this.router.url);
        this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event: Event) => {
            if (this.router.url !== '') {
                this.url = this.router.url;
                this.isFullScreenRoute = isFullScreenUrl(this.url);
                this.activeMenuOverride = '';
            }
        });

        this.subscription = this.dataService.activeMenuOverride.subscribe(data => {
            this.activeMenuOverride = data;
        });
    }

    @HostListener('window:focus', ['$event'])
    onFocus(event: any): void {
        if (this.isConnected) {
            this.getUserTzDetails();
            // // Refresh comment count when browser becomes active
            // this.getAllComments();
        }
    }

    /**
     * The app's ONLY GetNeedToRelinkUsers call. My Day used to make an identical one of its own, so a
     * full page load hit the endpoint twice; MineComponent now reads the answer off the data service
     * instead (see its ngOnInit).
     *
     * Publishes BOTH branches. The old body announced only the linked case — survivable only while
     * My Day was separately computing the unlinked one for itself. With that call gone, staying silent
     * on `false` would leave the M365 connect banner (mine.component.html:137) permanently hidden,
     * because `isAccLinked` defaults to true.
     *
     * Writes `dataService.isAccLinked` as well as emitting, because `isAccountLinked` is a plain
     * Subject with no replay: the field is the only way a component that mounts AFTER this response
     * lands — My Day reached by in-app navigation — can learn the answer.
     */
    private checkIsAccountLinked() {
        this._userService.getNeedToRelinkUsers().subscribe((result) => {
            if (!result.isError) {
                if (result.responseData) {
                    if (result.responseData.length > 0) {
                        result.responseData.forEach(x => {
                            if (x.emailAddress === this.appSession.user.emailAddress) {
                                this.isAccLinked = false;
                                this.dataService.isAccLinked = false;
                                this.dataService.isAccountLinked.next(false);
                            }
                        });
                    } else {
                        this.isAccLinked = true;
                        this.dataService.isAccLinked = true;
                        this.dataService.isAccountLinked.next(true);
                    }
                }
            }
        });
    }

    /**
     * E8: show the "Calendar" sidebar item (directly under My Day) only when Calendar is on — the FE
     * kill-switch plus the per-tenant master, the latter via getMySettings().featureEnabled (member-safe).
     * Drives the *ngIf on the Calendar button in the sidebar template.
     */
    private setupCalendarNav(): void {
        if (!AppConsts.calendarV1FeatureEnabled) {
            return;
        }
        this._myCalProxy.getMySettings().subscribe(settings => {
            this._calendarFeature.update(!!(settings && settings.featureEnabled));
        });
    }

    ngOnInit(): void {
        if (this.appSession.user) {
            this._getHelpState.init(this.appSession.user.id);
        }
        if (app.isInitialized()) {
            app.notifySuccess();
        }
        this._setupAppUpdateNotifications();
        // this.profileModal.isShownModal();
        if (this.appSession.application) {
            // The self-healing connection core lives entirely in UserSignalrService now — the
            // abp.signalr-client.js bootstrap (and its extra parallel connections) is gone.
            this._userSignalrService.init();
            this._userSignalrService.signalRConnectionState.subscribe(isConnected => {
            console.log('SignalR connection state:', isConnected ? 'Connected' : 'Disconnected');
        // Update UI accordingly
    });
}
        this.selectedDate = moment.parseZone(this.appSession.user.currentTimeAsUserTz);
        this.checkIsAccountLinked();
        this.setupCalendarNav();
        this.ttService.loadActiveTimer();
        //this.searchOrFilterSuggestions();
        // setTimeout(() => {
        //     $('.page-container').addClass('top-instruction');
        // }, 500);
        this.installationMode = UrlHelper.isInstallUrl(location.href);
        // NOTE: the auto-checkout "are you still working?" prompt was RETIRED (product decision 2026-08-07).
        // Its two subscriptions here had empty bodies, so they were already doing nothing; they and the
        // SignalR Subjects behind them are gone. Auto-checkout still happens server-side via
        // MarkUserAutoCheckOutWorker — users simply are not asked first. See CHECKIN_REMOVAL_TIERS.md §4c.

        // if (this.dataService.openHelpModal.observers.length === 0) {
        //     this.subscription = this.dataService.openHelpModal.subscribe(data => {
        //         this.vfHelpModal.show();
        //     });
        // }

        // openUpgradeModal is handled by <vf-billing-gate> (upgrade pop-up 1a, blocking pop-ups 1b/1c/1f).
        this.dataService.updateMeetingCount.subscribe(count => {
            this.todayMeetingsCount = count;
        });
        this.dataService.updateTodayTaskCount.subscribe(count => {
            this.todayTaskCount = count;
        });
        this._userSignalrService.taskUpdates
            .pipe(takeUntil(this.taskUpdates$))
            .subscribe((ev) => {
                switch (ev.actionPerformed) {
                    case 'tasknamechange':
                    case 'descriptionchange':
                    case 'datechange':
                    case 'projectassociationupdated':
                    case 'contactassociationupdated':
                    case 'tasktypeupdated':
                    case 'checklistchange':
                    case 'taskimportancechanged':
                    case 'updatetaskprivacy':
                    // A bulk action's cards, all but the last: its untagged last card refreshes once for all.
                    case 'bulkmarkcompleted':
                    case 'bulkchangeassignee':
                    case 'markalltaskstocomplete':
                    // The cards of the tasks a bulk complete unblocks; the action's untagged last card comes after them.
                    case 'bulktaskunblocked':
                    // My Day bulk Mark complete's schedule leg (EmailAutomationAppService.CompleteTasksInBulk). When no
                    // completion follows, its last schedule card is 'bulkfollowupscheduledlast' - deliberately NOT
                    // listed here, so it falls to default and refreshes the badge once for the whole action.
                    case 'bulkfollowupscheduled':
                        break;
                    default:
                        this.getAllNotificationCount();
                        break;
                }

            });

        // Auto-shift cascade undo toast (per Task Dependencies spec).
        // When changing a blocker's dates moves 2+ downstream blocked tasks forward,
        // TaskAppService.UpdateDates emits a `dependencyCascadeApplied` SignalR event.
        // We render a 10-second toast with an Undo link that calls UndoCascadeShift
        // to restore the previous dates of every shifted task.
        this._userSignalrService.cascadeAppliedUpdates
            .pipe(takeUntil(this.cascadeApplied$))
            .subscribe((payload) => {
                this.ngZone.run(() => this.showCascadeUndoToast(payload));
            });

        // Custom Project Label (Labels & Terminology): a tenant admin saved a new Project label.
        // Update the label store + the cached session tenant inside the Angular zone so every
        // {{ projectLabel }} binding across the app re-renders live, with no page refresh.
        this._userSignalrService.labelUpdates
            .pipe(takeUntil(this.labelUpdates$))
            .subscribe((details) => {
                this.ngZone.run(() => {
                    this.tenantLabelService.update(details.singular, details.plural);
                    if (this.appSession.tenant) {
                        this.appSession.tenant.projectLabelSingular = details.singular;
                        this.appSession.tenant.projectLabelPlural = details.plural;
                    }
                });
            });

        // Calendar & Events (E2): an admin toggled the workspace master switch -> show/hide the Calendar
        // nav live (the getter re-evaluates next CD cycle) and, if it was turned OFF while this user is on
        // the Calendar page, route them away so they don't sit on a dead surface.
        this._userSignalrService.calendarFeatureUpdates
            .pipe(takeUntil(this.calendarNav$))
            .subscribe((details) => {
                this.ngZone.run(() => {
                    this._calendarFeature.update(!!details.isEnabled);
                    if (!details.isEnabled && this.url === '/app/main/calendar') {
                        this.navigateMenu('/app/main/my-day');
                    }
                });
            });

        // Contributor-feature toasts. Skip events that the current user triggered.
        this._userSignalrService.contributorUpdates
            .subscribe((ev) => {
                if (!ev || ev.actorUserId === this.appSession.userId) {
                    return;
                }
                this.ngZone.run(() => {
                    this.handleContributorToast(ev);
                });
            });

        // Any favorites change refreshes the sidebar preview, whichever event carried it: getPinned for
        // adds / removes / reorders of every type (apps included — they used to be pinned only at
        // onboarding, so nothing announced a change to them before), and getPinnedProjectInfo for the
        // project paths that still emit it, namely unpin via PinUnpinProjects and drag-reordering the
        // project list's pinned group.
        //
        // Debounced because one action can produce several events: a project favorite arrives on both
        // channels (the service forwards getPinned onto pinnedProjectInfo), and a reorder emits one
        // event per moved row.
        //
        // Wrapped in NgZone.run: the signalr connection is created under runOutsideAngular, so these
        // handlers arrive with change detection off — and `debounceTime` does not put them back, since
        // its timer is scheduled in whatever zone the source emitted in. Without this the reload's
        // result never reached the sidebar's bindings, so a favorite added or removed from the projects
        // list, a contacts grid or another session left this preview stale until something unrelated
        // ticked change detection. The sidebar's own drag was never affected — that path runs in the
        // zone already — which is exactly why the gap was easy to miss.
        merge(this._userSignalrService.pinnedProjectInfo, this._userSignalrService.pinnedFavoriteInfo)
            .pipe(debounceTime(300), takeUntil(this.pinnedProjectInfo$))
            .subscribe(() => this.ngZone.run(() => {
                this.loadFavoritesPreview(true);
            }));

        this.subscription = this.dataService.clearNotification.subscribe(data => {
            if (data) {
                this.setNotificationsAsRead();
            }
        });

        this.subs.add(this.dataService.timerAutoStopped.subscribe(prev => {
            if (prev) {
                // Don't read elapsedSeconds$ here — by this point the new timer has
                // already started and reset the counter to 0. Derive the duration
                // from the previous timer's startedAt instead.
                let elapsedSec = 0;
                if (prev.startedAt) {
                    const startMs = new Date(prev.startedAt).getTime();
                    if (!isNaN(startMs)) {
                        elapsedSec = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
                    }
                }
                const elapsed = this.ttService.formatElapsed(elapsedSec);
                const msg = `Stopped timer on ${prev.taskName || 'previous task'} · ${elapsed} logged.`;
                this.toastr.info(msg, null, this.toastrOptions);
            }
        }));

        if (this.dataService.showBanner.observers.length === 0) {
            this.subscription = this.dataService.showBanner.subscribe(data => {
            });
        }


        if (this.dataService.closeOtherSidebar.observers.length === 0) {
            this.subscription = this.dataService.closeOtherSidebar.subscribe(data => {
                this.closeFile();
                this.closeContact();
                this.closeProject();
                this.closeSearch();
            });
        }

        if (this.dataService.removeRightSidebarClassIfNotNeeded.observers.length === 0) {
            this.subscription = this.dataService.removeRightSidebarClassIfNotNeeded.subscribe(data => {
                let isSidebarOpen = true;
                if (!$('#ProjectDetails').hasClass('show')) {
                    if ($('body').hasClass('rightsidebar-open')) {
                        $('body').removeClass('rightsidebar-open');
                    }
                }
                if (!$('#EditContactDetails').hasClass('show')) {
                    if ($('body').hasClass('rightsidebar-open')) {
                        $('body').removeClass('rightsidebar-open');
                    }
                }
                if (!$('#EditCompanyDetails').hasClass('show')) {
                    if ($('body').hasClass('rightsidebar-open')) {
                        $('body').removeClass('rightsidebar-open');
                    }
                }
            });
        }

        // getCurrentLoginInformations() was called a SECOND time here, after APP_INITIALIZER had
        // already populated appSession. It is not a pure read: each call also runs
        // Vf_Create_User_CheckInOut (a write), a Stripe GetUsedSeats/GetSubscription pair, ~10 EF
        // round-trips and a Clients.All SignalR broadcast. Its handler only set imageSource and
        // initials, neither of which is bound — the template reads getInitials(appSession.user...)
        // directly (app.component.html:256, :274).
        this._userService.getUserTimezoneInfo().subscribe((result) => {
            if (!result.isError) {
                let userTZ = result.responseData;
                this.currentUserTZ = userTZ.currentTimeAsUserTz as any;
            }
        });
        // Automatic My Day day-rollover — the replacement for the old check-in popup's full page reload.
        // Started from AppComponent because it is the always-mounted shell: a routed component would stop
        // watching the moment the user navigated away.
        this._dayRollover.start();

        this.subscription = this.dataService.isAccountLinked.subscribe(data => {
            this.isAccLinked = data;
        });
        this.subscription = this.dataService.closeProfile.subscribe(data => {
            this.closeProfileModal();
        });
        this.subscription = this.dataService.openUserGuideModal.subscribe(data => {
            this.showUserGuide();
        });
        this.subscription = this.dataService.sendAddTaskDetails.subscribe(data => {
            this.addTaskDetails = data;
        });
        this.subscription = this.dataService.attemptCloseAddTaskModal.subscribe(data => {
            this.openSaveChangesLostModal('addTaskClose');
        });

        this.getAllNotificationCount();
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            let leftMenuState = window.localStorage.getItem('leftMenuState');
            if (leftMenuState) {
                if (leftMenuState === 'expanded') {
                    $('.side-menu-left').addClass('expanded');
                } else if (leftMenuState === 'collapsed') {
                    $('.side-menu-left').removeClass('expanded');
                }
            } else {
                $('.side-menu-left').addClass('expanded');
            }
            // An expired firm (trial over, paused, ended) is blocked by <vf-billing-gate> itself.
        }, 200);
        this.loadFavoritesPreview();
        this.subs.add(
            this.dataService.refreshShortcuts.subscribe(() => {
                // One GetFavorites call covers both membership and order now, so the orderOnly flag
                // callers still send has nothing left to optimise away.
                this.loadFavoritesPreview(true);
            })
        );
        this.initFavoritesDragula();
    }

    /**
     * Reordering the sidebar's favorites by dragging, the same gesture the manager offers — the sidebar
     * IS the top of that one list, so the order it writes is the same order under TrSortOrders TblId 19.
     *
     * Dragula, as everywhere else in the app that reorders rows. The whole row is the handle, so
     * slideFactor keeps a click with a pixel of travel from being read as a drag and swallowed — the
     * same threshold, for the same reason, as the manager's list and the checklist in edit-task-modal.
     */
    private initFavoritesDragula(): void {
        if (this.dragulaService.find(this.FAV_DRAGULA)) {
            this.dragulaService.destroy(this.FAV_DRAGULA);
        }

        this.dragulaService.createGroup(this.FAV_DRAGULA, {
            slideFactorX: 10,
            slideFactorY: 10,
        } as any);

        this.subs.add(this.dragulaService.drag(this.FAV_DRAGULA)
            .subscribe(({ el }) => {
                el.classList.add('is-moving');
                // The hover callout is anchored to the row's rect, which is about to move out from
                // under it — and it sits above the mirror.
                this.hideFavTip();
            })
        );
        this.subs.add(this.dragulaService.dragend(this.FAV_DRAGULA)
            .subscribe(({ el }) => { el.classList.remove('is-moving'); })
        );
        this.subs.add(this.dragulaService.dropModel(this.FAV_DRAGULA)
            .subscribe(({ targetModel }) => { this.persistFavoritesOrder(targetModel); })
        );
    }

    /**
     * Writes the order the sidebar was just dragged into.
     *
     * Only the rows the sidebar holds are sent, numbered 0..n-1. That is the whole story for the server:
     * a drag up here can only permute the top of the list, everything below keeps the position it
     * already has, and ReorderFavorites upserts exactly the pins it is handed rather than renumbering
     * the rest. Positions are absolute and 0-based, matching what the manager writes.
     */
    private persistFavoritesOrder(rows: any[]): void {
        const input = new ReorderFavoritesInput();
        input.orders = (rows || [])
            .filter(r => r?._favEntityId > 0)
            .map((r, i) => {
                const order = new FavoriteOrderInput();
                order.type = r._favType;
                order.entityId = r._favEntityId;
                order.pinId = r._favPinId;
                order.sortOrder = i;
                return order;
            });

        if (input.orders.length === 0) {
            return;
        }

        this._favoriteService.reorderFavorites(input).subscribe({
            next: result => {
                if (result.isError) {
                    this.toastr.error(result.errorMessage || 'Could not save the new order.', null, this.toastrOptions);
                    this.loadFavoritesPreview(true);
                    return;
                }
                // The manager may be open on the same list — the side menu sits above its backdrop
                // deliberately, so both are usable at once — and it has to pick up the order written here.
                //
                // Emitted WITHOUT `skipModal`: that flag means "the shortcuts modal already knows", which is
                // true when the modal itself is the one emitting (its own sortFavorites does exactly that)
                // and false here. With it set, the modal's handler skipped the reload and kept showing the
                // old order until it was reopened.
                this.dataService.refreshShortcuts.next(null);
            },
            // Same reason as the manager's own reorder: a transport-level failure never reaches the
            // handler above, which only sees a business-level `isError`. Left unhandled, the row stayed
            // where it was dropped while the server still held the old order.
            error: () => {
                this.toastr.error('Could not save the new order.', null, this.toastrOptions);
                this.loadFavoritesPreview(true);
            }
        });
    }

    /**
     * The sidebar's favorites, from Favorite/GetFavorites — the same endpoint, membership and order the
     * manager uses, so the two lists cannot disagree.
     *
     * This replaced a union of four legacy loads (getProjectLists / getUserShortcuts / getAllContact /
     * getAllCompanies, 5000 rows each) whose result differed from the manager's: getUserShortcuts reads
     * TrUserShortcuts, which is quick-links membership rather than favorites, and it returns the same
     * TrLinks row more than once — which is where the repeated entries and the inflated "See all {n}"
     * came from. The manager reads GetFavorites only now too, so the two share one definition.
     *
     * Asks for exactly what it shows. The response carries the total beside the rows, so the count on
     * "See all {n}" and the fact that anything is hidden both come from the server — no need for the
     * extra probe row this used to fetch to tell that something was past the cutoff. The limit sent is
     * the sidebar's own; the server assumes nothing about it.
     */
    loadFavoritesPreview(silent = false): void {
        if (!silent) this.isFavOrderLoading = true;
        // No search term: the sidebar shows the top of the list, it is not a searchable surface.
        this._favoriteService.getFavorites(this.FAV_SIDEBAR_LIMIT, undefined)
            .subscribe(result => {
                this.isFavOrderLoading = false;
                const payload = !result.isError ? result.responseData : null;
                const items = payload?.items || [];
                this.favTotal = Number(payload?.totalCount) || items.length;
                this.hasHiddenFavorites = this.favTotal > this.FAV_SIDEBAR_LIMIT;
                this.favPreview = items
                    .slice(0, this.FAV_SIDEBAR_LIMIT)
                    .map((f: any) => this.toFavPreviewRow(f));
                this.resolveFavTrails(this.favPreview);
            });
    }

    /**
     * Fills in the location of every pinned file and folder, through FavoriteService.
     *
     * Only rows GetFavorites left without Segments are asked about. A link arrives with its project name
     * already in them, and that is the better answer for a link — so the lookup is a gap-filler, not an
     * override, and a link stored against a cloud file keeps its project rather than a drive trail.
     *
     * Called again for the hovered row when a tooltip opens: the load-time pass runs during app bootstrap,
     * and the service retries anything that failed there at the one moment its answer is wanted.
     */
    private resolveFavTrails(rows: any[]): void {
        const targets = (rows || []).filter(row => !row?._favSegments);

        if (targets.length === 0) {
            return;
        }

        this.subs.add(
            this._favorites.resolve(targets).subscribe(() => this.applyFavTrails())
        );
    }

    /** Writes every resolved trail onto the rows currently on screen. */
    private applyFavTrails(): void {
        this.favPreview.forEach(row => {
            const crumbs = this._favorites.trailOf(row);
            if (crumbs) {
                row._favSegments = crumbs;
            }
        });
    }

    /**
     * FavoriteItemOutput -> the shape the sidebar row and the open handlers already expect. Each opener
     * only needs an id (the modals refetch by it), so a row carries the ids for its kind and the
     * presentation fields the template keys on.
     *
     * The trail is taken straight off the DTO for every kind, so there is no client-side definition of
     * what a trail is at all: a project's client, a person's or company's type, an app's subtext, and —
     * since GetFavorites resolves it through GetBreadcrumbs before returning — a file's real location.
     * A row the server could not resolve one for shows its name alone.
     */
    private toFavPreviewRow(f: any): any {
        const segments = ((f.segments || []) as string[])
            .map(s => (s || '').trim())
            .filter(s => s.length > 0);

        const row = this.favPreviewShape(f);

        // A trail already resolved for this row's item wins over the server's Segments, and survives the
        // reload that rebuilt the row because the cache is keyed by item rather than by row object.
        return {
            ...row,
            // The pin's own identity, carried alongside the per-kind shape above: reordering sends
            // type + entity id per row, and the shapes differ too much to read it back out of them
            // (a project has projectId, a client has original.companyId, a file has id). Same three
            // fields the manager's rows carry, for the same reason.
            _favType: Number(f.type),
            _favEntityId: Number(f.entityId) || 0,
            _favPinId: Number(f.pinId) || 0,
            _favSegments: this._favorites.trailOf(row) || (segments.length > 0 ? segments : null)
        };
    }

    /** The per-kind ids and presentation fields. Split out so the trail above is set in one place. */
    private favPreviewShape(f: any): any {
        const type = Number(f.type);
        const entityId = Number(f.entityId) || 0;
        const pinId = Number(f.pinId) || 0;
        const name = f.name || '';

        // EnumTypes.EntityType — the value the pin carries. It is deliberately not a presentation
        // enum: File / Folder / Link are one type here, told apart by FileType below.
        switch (type) {
            case 2: // Project
                return { _favKind: 'project', projectId: entityId, projectName: name };
            case 5: // Contact
                return {
                    _favKind: 'client', _type: 'contact', name,
                    original: { id: entityId, pinnedEntityId: pinId }
                };
            case 7: // Company
                return {
                    _favKind: 'client', _type: 'company', name,
                    original: { companyId: entityId, pinnedEntityId: pinId }
                };
            case 4: // Apps
                // appIconUrl is the Microsoft tile the catalogue supplies. Without it the row fell through
                // to the generic lucide glyph, so every app favorite drew the same default icon.
                return { _favKind: 'app', id: entityId, name, url: f.url, appIconUrl: f.iconUrl };
            default: {
                // Attachment (8) and Link (3). Which table the row lives in does not decide how it
                // reads — a file is a file whether it was favorited as an attachment row or, before
                // the redirect, as a TrLinks row. FileType is what separates one from a web link.
                // isLink first: a web link stores FileType "Other", which reads as a file with no icon
                // to any extension test — which is why these rows came through the menu with none.
                const stored = this.normalizeFavExtension(f.fileType);
                const isFile = !f.isLink && (!!stored || !!f.msFileId);
                return {
                    _favKind: isFile ? 'file' : 'link',
                    id: entityId,
                    linkId: entityId,
                    name,
                    url: f.url,
                    path: f.path,
                    // The navigate-sub-folders modal opens a folder off these two.
                    driveId: f.msDriveId,
                    keyId: f.msFileId,
                    // The containing folder, which is what GetBreadcrumbs walks up from to build the
                    // trail. Carried for that alone — nothing else on the sidebar row reads them.
                    siteId: f.msSiteId,
                    folderId: f.msFolderId,
                    extension: stored || (isFile ? this.favExtensionOf(name) : '')
                };
            }
        }
    }

    /**
     * The row icon keys on this. A stored FileType may be a FileInfo extension — ".docx", leading dot
     * and all — while the icon lookup expects the bare "docx".
     *
     * Case is left alone on purpose: isFavFolderItem matches "documentLibrary" exactly, and the icon
     * and image lookups lower-case what they are given anyway.
     */
    private normalizeFavExtension(fileType: string): string {
        const ext = (fileType || '').trim();
        return ext.startsWith('.') ? ext.substring(1) : ext;
    }

    /** Fallback for a file row whose FileType came back empty: read it off the name. */
    private favExtensionOf(name: string): string {
        const dot = (name || '').lastIndexOf('.');
        return dot > 0 ? name.substring(dot + 1).toLowerCase() : '';
    }

    /**
     * The "Add to favorites" menu, anchored to the sidebar's + button as in the design. Five kinds,
     * ordered by how often each gets pinned — File first, not Project.
     * `app` has no add path: a user's apps are seeded by VF_Create_Default_Apps and can only be
     * deleted, so the item is present to match the design but disabled rather than dead.
     */
    get favAddTypes(): Array<{ key: string; label: string; icon: string; disabled: boolean; hint?: string }> {
        return [
            { key: 'file', label: 'File', icon: 'file', disabled: false },
            { key: 'project', label: this.projectLabel, icon: 'briefcase', disabled: false },
            { key: 'client', label: 'Client', icon: 'building', disabled: false },
            { key: 'app', label: 'App', icon: 'layout-grid', disabled: false },
            { key: 'link', label: 'Link', icon: 'link', disabled: false },
        ];
    }

    // Fixed-position, anchored to the + button. The sidebar nav is an overflow:auto scroller, so an
    // absolutely-positioned menu gets clipped at its edge — the design anchors with fixed coords too.
    favAddMenuStyle: { [k: string]: string } = {};

    toggleFavAddMenu(event: MouseEvent): void {
        event.stopPropagation();
        if (this.showFavAddMenu) {
            this.showFavAddMenu = false;
            return;
        }
        const MENU_W = 176;
        const MENU_H = 214;
        const r = (event.currentTarget as HTMLElement).getBoundingClientRect();
        const below = r.bottom + 6;
        const top = (below + MENU_H > window.innerHeight) ? Math.max(8, r.top - MENU_H - 6) : below;
        this.favAddMenuStyle = {
            top: `${Math.round(top)}px`,
            left: `${Math.round(Math.min(r.right - MENU_W + 14, window.innerWidth - MENU_W - 8))}px`,
        };
        this.showFavAddMenu = true;
    }

    /** Opens the manager straight into the add flow for the chosen kind. */
    pickFavoriteAddType(type: { key: string; disabled: boolean }): void {
        if (type.disabled) {
            return;
        }
        this.showFavAddMenu = false;
        this.openShortcut(undefined, { addType: type.key });
    }

    // Keyed on the entity: pinId is 0 for every file/folder/app, which would collide across rows.
    trackByFavItem = (_: number, item: any): string => this.favKeyOfItem(item);

    trackByFavAddType = (_: number, t: { key: string }): string => t.key;

    // Position-keyed: two parts of a trail can read the same ("Shared › Shared").
    trackByFavTipCrumb = (index: number, crumb: string): string => `${index}:${crumb}`;

    /**
     * Identity shared with the server's EntityType, collapsed to the group the client can rebuild:
     * attachment-backed rows and quick links key off their own entity id together.
     */
    private favKeyOfItem(item: any): string {
        switch (item?._favKind) {
            case 'project':
                return `project:${Number(item.projectId) || 0}`;
            case 'client':
                return item._type === 'company'
                    ? `company:${Number(item.original?.companyId) || 0}`
                    : `person:${Number(item.original?.id) || 0}`;
            case 'app':
                return `app:${Number(item.id) || 0}`;
            default:
                return `link:${Number(item.linkId ? item.linkId : item.id) || 0}`;
        }
    }

    // ── Sidebar favorite hover callout ──────────────────────────────────────────────────────────
    // The row shows one truncated line, which is not enough to tell "Contract.docx" in one client's
    // folder from the same name in another's. Hovering names the favorite in full and, under it, the
    // trail the manager's row shows as its subtitle.
    //
    // Fixed coordinates computed off the row's rect, as the add menu does, because .sidebar-nav-scroll
    // would clip an absolutely-positioned callout at its edge.
    favTipItem: any = null;
    favTipStyle: { [k: string]: string } = {};

    /** Matches the design's sidebar tooltip: long enough not to flash while the pointer crosses rows. */
    private readonly FAV_TIP_DELAY_MS = 200;
    private favTipTimer: number = null;

    showFavTip(event: MouseEvent, item: any): void {
        this.clearFavTipTimer();
        // Second chance for a row whose trail did not resolve during bootstrap. Hovering is a deliberate
        // act, so it is also where an item the service gave up on is allowed another attempt — the render
        // paths must not do that, or the retry becomes a request per render. Cached and in-flight items
        // are skipped inside, so hovering a resolved row still sends nothing.
        this._favorites.retryUnresolved();
        this.resolveFavTrails([item]);
        const target = event.currentTarget as HTMLElement;
        this.favTipTimer = window.setTimeout(() => {
            const r = target.getBoundingClientRect();
            this.favTipStyle = {
                top: `${Math.round(r.top + r.height / 2)}px`,
                left: `${Math.round(r.right + 10)}px`,
                transform: 'translateY(-50%)',
            };
            this.favTipItem = item;
        }, this.FAV_TIP_DELAY_MS);
    }

    hideFavTip(): void {
        this.clearFavTipTimer();
        this.favTipItem = null;
    }

    private clearFavTipTimer(): void {
        if (this.favTipTimer !== null) {
            clearTimeout(this.favTipTimer);
            this.favTipTimer = null;
        }
    }

    /** Routes a sidebar favorite to the right opener now that one list holds every kind. */
    openFavItem(item: any): void {
        // The pointer leaves the row without a mouseleave when the panel opens over it.
        this.hideFavTip();
        switch (item?._favKind) {
            case 'project':
                this.openFavProject(item);
                return;
            case 'client':
                this.openFavClient(item);
                return;
            default:
                this.openFavFileLink(item);
                return;
        }
    }

    isFavFolderItem(item: any): boolean {
        return item?.extension === 'documentLibrary'
            || item?.extension === 'folder'
            || (!item?.extension && !!item?.keyId && !!item?.driveId);
    }

    isFavImageItem(item: any): boolean {
        const extension = (item?.extension || '').toLowerCase();
        return extension === 'jpg'
            || extension === 'jpeg'
            || extension === 'gif'
            || extension === 'bmp'
            || extension === 'svg'
            || extension === 'png';
    }

    private getAllNotificationCount() {
        this._notificationService.getUnReadNotificationsCounts().subscribe(result => {
            if (!result.isError) {
                // Also called from the SignalR task-card handler, which runs outside Angular's zone.
                this.ngZone.run(() => {
                    this.newTaskCount = result.responseData ? result.responseData > 99 ? '99+' : result.responseData.toString() : '0';
                });
            }
        });
    }

    ngAfterViewChecked(): void {
        $('.open-rightside-apps').hover(function () {
            $('#appsicon').attr('src', 'https://cdn.trovve.com/vfapp/images/icon-apps-green.svg');
        }, function () {
            $('#appsicon').attr('src', 'https://cdn.trovve.com/vfapp/images/icon-apps-unselected.svg');
        });

    }

    showUserGuide() {
        if (this.appSession.user.isPromoVisited === false) {
            setTimeout(() => {
                this.showPinOBModal = true;
                this._userService.updateIsPromoVisited().subscribe((result) => {
                    this._appSessionService.init().then(() => {
                    });
                });
            }, 1000);
        } else {
            this.showPinOBModal = true;
        }
    }

    closePinOBModal() {
        this.showPinOBModal = false;
    }

    searchOrFilterSuggestions(): void {
        let strDt = this.selectedDate.format('MM/DD/YYYY').toString();
        this._taskService.getMySuggestedTasks(0, 100, strDt, false, false, false, false, '')
            .subscribe(suggestionData => {
                this.newTaskData = [];
                if (!suggestionData.isError) {
                    if (suggestionData.responseData) {

                        if (suggestionData.responseData['item1'].length > 0) {
                            let tmpNewTask = suggestionData.responseData['item1'].filter(x => x.isSeen === false);
                            this.newTaskData = cloneDeep(tmpNewTask);
                            if (this.newTaskData && this.newTaskData.length > 0) {
                                // alert('d');
                                // if (!$('.open-rightside-tasks').hasClass('active')) {
                                // this.isNewTask = true;
                                // } else {
                                //     this.isNewTask = false;
                                // }
                            }
                        }
                    }
                }
            });
    }


    private setNotificationsAsRead(): void {
        let input: SetNotificationReadInput = new SetNotificationReadInput();
        input.markAllRead = true;
        this._notificationService.setNotificationsAsRead(input).subscribe((result) => {
            if (!result.isError) {
                this.getAllNotificationCount();
            }
        });
    }

    private getUserTzDetails() {
        let userTimezoneOffset = this.appSession.user.userTimeZoneOffset;
        this.currentUserTZ = moment(new Date()).utc().utcOffset(userTimezoneOffset);
    }

    closeBanner() {
        $('.page-container-new').removeClass('top-instruction');
    }

    relink() {
        this.dataService.showTokenLink.next();
    }
    subscriptionStatusBarVisible(): boolean {
        return this._appSessionService.tenantId > 0 &&
            (this._appSessionService.tenant.isInTrialPeriod ||
                this.subscriptionIsExpiringSoon());
    }

    subscriptionIsExpiringSoon(): boolean {
        if (this._appSessionService.tenant.subscriptionEndDateUtc) {
            return moment().utc().add(AppConsts.subscriptionExpireNootifyDayCount, 'days') >= moment(this._appSessionService.tenant.subscriptionEndDateUtc);
        }

        return false;
    }

    setActiveTab(): void {
        this.dataService.settingCurrentTabChange.next(1);
    }

    /**
     * Full page reload, clearing the slider state first.
     *
     * Named for the "New version available!" banner, which is its REAL owner and must keep working — a
     * new bundle genuinely requires a reload. The check-in popup's "Refresh my workspace" button also
     * still calls this while that markup exists (T4a removes it); the popup is suppressed whenever
     * AppConsts.dayRolloverV1FeatureEnabled is true, so in practice only the version banner reaches it.
     *
     * Previously called reloadAppForNewCheckin(), which read as check-in-only and made it look safe to
     * delete alongside the popup. Deleting it would have silently broken version updates.
     */
    reloadAppForNewVersion() {
        window.localStorage.setItem('sliderState', '[]');
        location.reload();
    }

    refreshBrowser() {
        this.refrshBtn = false;
        $('.page-container-new').removeClass('top-instruction');
        location.reload();
    }

    onSuccessUpgrade(event) {
        this.closeBanner();
        this.successUpgrade.show();
        this.updateBodyForCenterModalShow();
    }

    closeUpgradeSuccessModal(): void {
        this.successUpgrade.hide();
        this.updateBodyForCenterModalHide();
        this.openUpg();
    }

    onCloseUpgrade(event) {
        this.closeBanner();
    }

    /** The upgrade pop-up (1a) lives in <vf-billing-gate>, which listens on this existing channel (Teams included). */
    openUpgradeModal() {
        this.closeBanner();
        this.dataService.openUpgradeModal.next(true);
    }

    openUpg() {
        this.dataService.openUpgModal.next();
    }

    openProfile(result: boolean) {
        if (result) {
            if ($('#OpenProfileModal').length === 0 || !$('#OpenProfileModal').hasClass('show')) {
                this.closeFile();
                // this.closeContact();
                this.closeProject();
                this.closeQuickAddForms();
                this.dataService.closeSubMenuItems.next('profile');
                this.profileModal.show();
            } else {
                this.profileModal.close();
            }
        }
    }

    /**
     * FU-Z4-FIX FZ-3: the profile menu closes and its button takes the focus BEFORE the drawer opens, so the drawer
     * remembers the button (not the menu item, which leaves the page) and gives the focus back to it on close.
     */
    openConnectedApps(): void {
        if (this.profileOptionsPopover) {
            this.profileOptionsPopover.close();
        }
        if (this.profileOptionsButton && this.profileOptionsButton.nativeElement) {
            this.profileOptionsButton.nativeElement.focus({ preventScroll: true });
        }
        if (this.connectedAppsModal) {
            this.connectedAppsModal.show();
        }
    }

    private rightSideBarOpenClassToggle() {
        // adding right side bar class if any of the Quick Add form is open.
        if ($('#AddProject').length > 0 ||
            $('#AddMeeting').length > 0 ||
            $('#OpenAddTask').length > 0 ||
            $('#addContactModal').length > 0 ||
            $('#AddQuickLink').length > 0) {
            if (!$('body').hasClass('rightsidebar-open')) {
                $('body').addClass('rightsidebar-open');
            }
        } else {
            $('body').removeClass('rightsidebar-open');
        }
    }

    openProject(result?: any) {
        if (this.isAnyUnsavedFormOpen()) {
            this.openSaveChangesLostModal('projectsSidebar');
            return;
        }

        if ($('#ProjectDetails').length > 0) {
            this.dataService.closeSubMenuItems.next('profile');
            if ($('.right-side-bar-projects').length === 0 || $('.right-side-bar-projects').hasClass('collapsed')) {
                this.closeFile();
                // this.closeContact();
                this.closeQuickAddForms();
                this.closeProfileModal();
                this.closeSearch();
                this.dataService.openProjectList.next(false);
            }
        } else {
            this.isMenuClicked = true;
            if ($('.right-side-bar-projects').length === 0 || $('.right-side-bar-projects').hasClass('collapsed')) {
                this.closeFile();
                // this.closeContact();
                this.closeQuickAddForms();
                this.closeProfileModal();
                this.closeSearch();
                this.dataService.openProjectList.next(false);
            } else {
                this.closeProject();
                this.rightSideBarOpenClassToggle();
            }

            setTimeout(() => {
                this.isMenuClicked = false;
            }, 400);
        }

    }

    openContact(view?: any) {
        if (this.isAnyUnsavedFormOpen()) {
            this.openSaveChangesLostModal('contactsSidebar');
            return;
        }
        this.isMenuClicked = true;
        this.closeFile();
        this.closeQuickAddForms();
        this.closeProfileModal();
        this.closeSearch();
        this.dataService.openContactList.next(view);
        setTimeout(() => {
            this.isMenuClicked = false;
        }, 400);
    }

    openFiles(result?: any) {
        if (this.isAnyUnsavedFormOpen()) {
            this.openSaveChangesLostModal('filesSidebar');
            return;
        }
        this.isMenuClicked = true;
        if ($('.right-side-bar-files').length === 0 || $('.right-side-bar-files').hasClass('collapsed')) {
            // this.closeContact();
            this.closeQuickAddForms();
            this.closeProfileModal();
            this.closeSearch();
            this.dataService.closeEditTaskModalOnLeft.next();
            this.dataService.openFileList.next();
        } else {
            this.closeFile();
            this.rightSideBarOpenClassToggle();
        }
        setTimeout(() => {
            this.isMenuClicked = false;
        }, 400);
    }

    /**
     * The sidebar Conversations item: All scope. The service gates on Pages.Agenda and routes to My Day from
     * /app/settings. On that route, clear 'sliderState' first (as navigateMenu does): otherwise AgendaComponent
     * replays a panel left open before Settings ~200 ms after mount, and MainLayout's close('destination') for that
     * panel closes the overlay the pending request has just opened (GC-N-20).
     */
    openConversations(): void {
        if (this.router.url.startsWith('/app/settings')) {
            window.localStorage.setItem('sliderState', '[]');
        }
        this._conversationsOverlay.open({ scope: 'all', origin: 'nav' });
    }

    openShortcut(result?: any, options?: { addType?: string }) {
        if (this.isAnyUnsavedFormOpen()) {
            this.openSaveChangesLostModal('shortcutSidebar');
            return;
        }
        this.isMenuClicked = true;
        this.isCalOpen = true;
        if ($('#shortcutsModal').length === 0) {
            this.closeFile();
            // this.closeContact();
            this.closeQuickAddForms();
            this.closeProfileModal();
            this.closeSearch();
            this.dataService.closeEditTaskModalOnLeft.next();
            this.dataService.openShortcutsModal.next(options);
        } else if (options?.addType) {
            // Already mounted, but this is an explicit "add a favorite of kind X" request rather than
            // a toggle — re-announce so the manager starts that add flow instead of closing.
            this.dataService.openShortcutsModal.next(options);
        } else {
            this.dataService.closeTertiaryItems.next();
            this.rightSideBarOpenClassToggle();
        }
        setTimeout(() => {
            this.isMenuClicked = false;
        }, 400);
    }

    checkUnsavedForPopoverOpen(from: string): void {
        if (this.isAnyUnsavedFormOpen()) {
            this.openSaveChangesLostModal(from);
        }
    }


    isAnyUnsavedFormOpen(): boolean {
        return $('#CreateProjTemplateModal').length > 0 || $('#OpenAddTask').length > 0;
    }

    openSaveChangesLostModal(from: string): void {
        console.log('Unsaved changes detected, opening modal for:', from);
        // Logic to open modal warning about unsaved changes
        if ($('#CreateProjTemplateModal').length > 0) {
            $('#UnsavedDataLostModal').modal('show');
        }
        if ($('#OpenAddTask').length > 0) {
            this.dataService.getDetailsAddTaskModal.next();
            setTimeout(() => {
                let hasTitle: boolean = false;
                let hasDescription: boolean = false;

                hasTitle = this.addTaskDetails.taskTitle !== '' && this.addTaskDetails.taskTitle !== undefined;
                hasDescription = this.addTaskDetails.taskDescription !== '' && this.addTaskDetails.taskDescription !== undefined && this.addTaskDetails.taskDescription !== null && this.addTaskDetails.taskDescription.trim() !== '';

                if (hasTitle || hasDescription) {
                    $('#AddTaskUnsavedDataLostModal').modal('show');
                } else {
                    this.closeUnsavedChangesModal();
                }
            }, 50);
        }
        this.openedUnsavedDataLostModalFrom = from;
    }

    closeUnsavedDataLostModal(): void {
        console.log('Discarding unsaved changes');
        $('#UnsavedDataLostModal').modal('hide');
        $('#AddTaskUnsavedDataLostModal').modal('hide');
    }

    saveAddTaskDetails(type: number): void {
        this.dataService.saveDetailsAddTaskModal.next(type);
        $('#AddTaskUnsavedDataLostModal').modal('hide');
    }

    closeUnsavedChangesModal(): void {
        console.log('Closing unsaved changes modal');
        $('#UnsavedDataLostModal').modal('hide');
        $('#AddTaskUnsavedDataLostModal').modal('hide');
        this.dataService.closeCreateUpdateProjTemplateModal.next();
        this.dataService.closeEditTaskFromTemplateModal.next();
        this.dataService.closeAddTaskModal.next();
        if (this.openedUnsavedDataLostModalFrom === 'globalSearch') {
            setTimeout(() => {
                this.openGlobalSearch();
            }, 210);
        } else if (this.openedUnsavedDataLostModalFrom === 'filesSidebar') {
            setTimeout(() => {
                this.openFiles();
            }, 210);
        } else if (this.openedUnsavedDataLostModalFrom === 'projectsSidebar') {
            setTimeout(() => {
                this.openProject();
            }, 210);
        } else if (this.openedUnsavedDataLostModalFrom === 'contactsSidebar') {
            setTimeout(() => {
                this.openContact();
            }, 210);
        } else if (this.openedUnsavedDataLostModalFrom === 'quickAdd') {
            if (this.quickAddPopover) {
                setTimeout(() => {
                    this.quickAddPopover.open();
                }, 210);
            }
        } else if (this.openedUnsavedDataLostModalFrom === 'contactsAction') {
            if (this.contactPopover) {
                setTimeout(() => {
                    this.contactPopover.open();
                }, 210);
            }
        } else if (this.openedUnsavedDataLostModalFrom === 'extraActions') {
            if (this.extraActionsPopover) {
                setTimeout(() => {
                    this.extraActionsPopover.open();
                }, 210);
            }
        } else if (this.openedUnsavedDataLostModalFrom === 'helpActions') {
            if (this.helpActionsPopover) {
                setTimeout(() => {
                    this.helpActionsPopover.open();
                }, 210);
            }
        } else if (this.openedUnsavedDataLostModalFrom === 'profileOptions') {
            if (this.profileOptionsPopover) {
                setTimeout(() => {
                    this.profileOptionsPopover.open();
                }, 210);
            }
        }
    }

    openGlobalSearch(result?: any) {
        if (this.isAnyUnsavedFormOpen()) {
            this.openSaveChangesLostModal('globalSearch');
            return;
        }
        this.isMenuClicked = true;
        if ($('.right-side-bar-search').length === 0 || $('.right-side-bar-search').hasClass('collapsed')) {
            this.closeFile();
            // this.closeContact();
            this.closeQuickAddForms();
            this.closeProfileModal();
            this.dataService.openGlobalSearchMoreResultsModal.next('');
        } else {
            this.closeSearch();
            this.rightSideBarOpenClassToggle();
        }
        setTimeout(() => {
            this.isMenuClicked = false;
        }, 400);
    }

    openSettingsMenu(): void {
        $('.open-settings-menu').addClass('active');
    }

    closeSettingsMenu(): void {
        $('.open-settings-menu').removeClass('active');
    }

    closeProject() {
        if (!$('.right-side-bar-projects').hasClass('collapsed')) {
            this.dataService.closeProjectList.next();
        }
    }

    closeContact() {
        if (!$('.right-side-bar-contacts').hasClass('collapsed')) {
            this.dataService.closeContactList.next();
        }
    }

    closeFile() {
        if (!$('.right-side-bar-files').hasClass('collapsed')) {
            this.dataService.closeFileExpolorerModal.next();
            this.dataService.closeFileList.next();
        }
    }

    closeSearch() {
        this.dataService.closeGlobalSearch.next();
    }

    closeQuickAddForms(): void {
        this.closeQuickAddTask();
        this.closeQuickAddContact();
        this.closeQuickAddProject();
        this.closeQuickAddMeeting();
        this.closeQuickAddLink();
    }

    closeQuickAddTask(): void {
        this.dataService.closeQuickAddTaskForm.next();
    }

    closeQuickAddMeeting(): void {
        this.dataService.closeQuickAddMeetingForm.next();
    }

    closeQuickAddProject(): void {
        this.dataService.closeQuickAddProjectForm.next();
    }

    closeQuickAddLink(): void {
        this.dataService.closeQuickAddLinkForm.next();
    }

    closeQuickAddContact(): void {
        this.dataService.closeQuickAddContactForm.next();
    }

    removeBodyClasses(): void {
        $('body').removeClass('rightsidebar-open');
    }

    routeToSettings(): void {
        window.localStorage.setItem('routeToSettings', 'true');
        if (this.router.url.startsWith('/app/settings')) {
            this.dataService.settingCurrentTabChange.next(1);
        } else {
            window.localStorage.setItem('settingsTab', '1');
            this.router.navigateByUrl('/app/settings');
        }
    }

    routeToCompanySettings(): void {
        window.localStorage.setItem('routeToSettings', 'true');
        if (this.router.url.startsWith('/app/settings')) {
            this.dataService.settingCurrentTabChange.next(5);
        } else {
            window.localStorage.setItem('settingsTab', '5');
            this.router.navigateByUrl('/app/settings');
        }
    }

    /**
     * Profile menu → Workspace → Intelligence (D18, IN-N-01), for every user: Settings on its "Intelligence" tab (9),
     * the same way as the other settings shortcuts (the settingsTab key, or the existing settingCurrentTabChange when
     * Settings is already open). settings-nav keeps the tab in the URL, so F5 stays on it (IN-N-04).
     */
    routeToIntelligence(): void {
        window.localStorage.setItem('routeToSettings', 'true');
        if (this.router.url.startsWith('/app/settings')) {
            this.dataService.settingCurrentTabChange.next(INTELLIGENCE_SETTINGS_TAB);
        } else {
            window.localStorage.setItem('settingsTab', String(INTELLIGENCE_SETTINGS_TAB));
            this.router.navigateByUrl('/app/settings');
        }
    }

    routeToBillingAndPlan(): void {
        window.localStorage.setItem('routeToSettings', 'true');
        if (this.router.url.startsWith('/app/settings')) {
            this.dataService.settingCurrentTabChange.next(6);
        } else {
            window.localStorage.setItem('settingsTab', '6');
            this.router.navigateByUrl('/app/settings');
        }
    }

    routeToEmailAutomation(): void {
        window.localStorage.setItem('routeToSettings', 'true');
        if (this.router.url.startsWith('/app/settings')) {
            this.dataService.settingCurrentTabChange.next(7);
        } else {
            window.localStorage.setItem('settingsTab', '7');
            this.router.navigateByUrl('/app/settings');
        }
    }

    routeToCalendar(): void {
        window.localStorage.setItem('routeToSettings', 'true');
        if (this.router.url.startsWith('/app/settings')) {
            this.dataService.settingCurrentTabChange.next(8);
        } else {
            window.localStorage.setItem('settingsTab', '8');
            this.router.navigateByUrl('/app/settings');
        }
    }

    browseHelpArticles(): void {
        window.open('https://help.trovve.com', '_blank');
    }

    routeToMyDay(): void {
        window.localStorage.setItem('sliderState', '[]');
        this.dataService.closeMainMenuItems.next();
        this.dataService.closeSubMenuItems.next();
        this.dataService.closeTertiaryItems.next();
        this.router.navigateByUrl('/app/main/my-day');
    }

    routeToProjects(): void {
        window.localStorage.setItem('sliderState', '[]');
        this.dataService.closeMainMenuItems.next();
        this.dataService.closeSubMenuItems.next();
        this.dataService.closeTertiaryItems.next();
        this.router.navigateByUrl('/app/main/projects');
    }

    routeToClients(): void {
        window.localStorage.setItem('sliderState', '[]');
        this.dataService.closeMainMenuItems.next();
        this.dataService.closeSubMenuItems.next();
        this.dataService.closeTertiaryItems.next();
        this.router.navigateByUrl('/app/main/clients');
    }

    openFavProject(project: any): void {
        this.dataService.closeTertiaryItems.next();
        this.dataService.openViewProject.next(project);
    }

    openFavClient(client: any): void {
        this.dataService.closeTertiaryItems.next();
        if (client._type === 'company') {
            this.dataService.openEditCompanyModal.next(client.original);
        } else {
            this.dataService.openEditContactModal.next(client.original);
        }
    }

    openFavFileLink(item: any): void {
        // Folders and document libraries should open in the in-app navigate-sub-folders modal
        if (this.isFavFolderItem(item)) {
            this.dataService.openNavigationModal.next({ ...item, from: 'Favorites' });
            return;
        }
        const url = item.url || item.webUrl;
        if (url) {
            window.open(url, '_blank');
        }
    }

    addProject(): void {
        // this.dataService.openAddProjectModal.next();
        // this.dataService.openCreateProjOptionsModal.next();
        this.dataService.openAddProjectModal.next(undefined);
        this.closeQuickAddTask();
        this.closeQuickAddMeeting();
        this.closeQuickAddLink();
        this.closeQuickAddContact();
    }

    addContact(): void {
        this.dataService.openAddContactModal.next();
        this.closeQuickAddTask();
        this.closeQuickAddMeeting();
        this.closeQuickAddProject();
        this.closeQuickAddLink();
    }

    addCompany(): void {
        this.dataService.openAddCompanyModal.next();
        this.closeQuickAddTask();
        this.closeQuickAddMeeting();
        this.closeQuickAddProject();
        this.closeQuickAddLink();
    }

    addTask(): void {
        let projObj: any = {};
        const currentProject = this.dataService.currentViewProject;
        if (currentProject && currentProject.projectId) {
            projObj.id = currentProject.projectId;
            projObj.name = currentProject.projectName;
            projObj.isAutoAdd = currentProject.isAutoAdd;
            projObj.haveChecklistTemplate = currentProject.haveChecklistTemplate;
            projObj.sectionId = null;
            projObj.sectionName = null;
        }
        this.dataService.openAddTaskModal.next(['', [], projObj, 0]);
    }

    /** Quick-add "Event": open the reusable create-event form in place via EventFormService — no route
     *  change, so it works from the project modal, my-day, projects, etc. The active route-shell
     *  (Agenda / WorkspaceCalendar) hosts the panel and responds. When a project modal is open,
     *  `currentViewProject` is set (cleared on close), so the new event prefills that project — mirrors
     *  the "Create new → Task" behaviour. */
    addEvent(): void {
        const p = this.dataService.currentViewProject;
        this._eventForm.createEvent({
            date: new Date(),
            projectId: p && p.projectId ? p.projectId : null,
            projectName: p && p.projectName ? p.projectName : null,
        });
    }

    openHelpArticles() {
        window.open('https://help.trovve.com', '_blank');
    }

    /**
     * A click on a main-menu navigation item (routes and favorites). Lets the help introduction move into Get help for
     * existing users who have not engaged with it — without delaying the navigation itself.
     */
    menuNavigate(route: string): void {
        this._getHelpState.onMainMenuNavigate();
        this.navigateMenu(route);
    }

    menuOpenFavItem(item: any): void {
        this._getHelpState.onMainMenuNavigate();
        this.openFavItem(item);
    }

    /** Main-menu Get help: a draft to help@trovve.com in the user's own mail app (the banner form is not reopened here). */
    openContactSupport() {
        openSupportEmail();
    }

    bookCall() {
        window.open('https://calendly.com/trovve/trovve-support', '_blank');
    }

    signOut() {
        window.localStorage.setItem('sliderState', '[]');
        this._authService.logout();
    }

    closeProfileModal() {
        this.profileModal.close();
    }

    startOB() {
        this.dataService.closeEditTaskModalOnLeft.next();
        this.dataService.closeMainMenuItems.next();
        this.dataService.closeSubMenuItems.next();
        this.dataService.closeTertiaryItems.next();
        setTimeout(() => {
            $('body').removeClass('rightsidebar-open');
            $('body').removeClass('task-modal-body');
            this.showPinOBModal = true;
        }, 500);
    }

    addRecurring() {
        this.dataService.openRecurringTemplateListModal.next();
    }

    /**
     * Render the cascade-shift undo toast AND/OR a conflict-notice toast.
     *
     * Per the Task Dependencies spec:
     *   • "When shifting a task causes 2 or more downstream tasks to move, show a
     *     toast: 'Shifting [Task A] also moved [N] downstream tasks. Undo?' with a
     *     10-second window."
     *   • "Skip In Progress and Completed tasks — they don't auto-shift, they flag
     *     a conflict instead."
     *
     * The BE bundles both lists onto a single `dependencyCascadeApplied` SignalR
     * event so the FE can render whichever subset applies — they're independent:
     *   • shifts ≥ 1  → undo toast (info, 10s, with Undo button)
     *   • conflicts ≥ 1 → conflict toast (warning, 10s, no action button)
     * Even a single downstream shift shows the undo toast (threshold lowered from ≥2 per user request 2026-06-08).
     */
    private showCascadeUndoToast(payload: any): void {
        // The BE emits this SignalR event from three sites with INCONSISTENT property
        // casing (PascalCase at TaskAppService.cs 1087 & 7538, camelCase at ~11916).
        // The hub protocol normally camelCases, but read both so a casing change at the
        // source can never silently drop the toast (the historical "blank/no toast" bug).
        const affected = (payload && (payload.affectedTasks || payload.AffectedTasks)) || [];
        const conflicts = (payload && (payload.conflictedTasks || payload.ConflictedTasks)) || [];
        // Whole-batch undo: the originally-moved task(s) ship alongside the downstream shifts so
        // clicking Undo rolls back the user's move too, not just the cascade. Read both casings.
        const triggerTasks = (payload && (payload.triggerTasks || payload.TriggerTasks)) || [];
        const triggerTitle: string = payload?.triggerTitle || payload?.TriggerTitle || 'the blocker';

        if (affected.length >= 1) {
            this.renderCascadeShiftToast(triggerTitle, affected, triggerTasks);
        }
        if (conflicts.length >= 1) {
            this.renderCascadeConflictToast(conflicts);
        }
    }

    /**
     * The undo-shift toast. Uses the app's standard `buttons + onAction` pattern so
     * the Undo only fires when the user clicks the Undo button (not anywhere on the
     * toast body — that was the bug we fixed for the dependency remove toast).
     */
    private renderCascadeShiftToast(triggerTitle: string, affected: any[], triggerTasks: any[] = []): void {
        const count = affected.length;
        const message = `Shifting ${triggerTitle} also moved ${count} downstream task${count === 1 ? '' : 's'}.`;

        const options: any = cloneDeep(this.toastrOptions);
        options.timeOut = 10000;       // 10-second window per spec
        // extendedTimeOut must be > 0: with 0, ngx-toastr never re-arms the hide timer after the
        // user hovers the toast, so it sticks forever. Keep the Undo button as the explicit action;
        // tapToDismiss stays false so a stray body click can't lose the undo before they decide.
        options.extendedTimeOut = 5000;
        options.tapToDismiss = false;
        options.enableHtml = true;
        options.buttons = [{ id: '1', title: 'Undo' }];

        const toast = this.toastr.info(message, null, options);
        if (toast && (toast as any).onAction) {
            // Restore the whole batch on Undo: the trigger task(s) first, then the downstream shifts.
            (toast as any).onAction.subscribe(() => this.undoCascadeShift([...triggerTasks, ...affected]));
        }
    }

    /**
     * The conflict-notice toast. Lists the In Progress / Completed downstream tasks
     * that the cascade refused to shift (the user-locked dates win). No Undo —
     * nothing was changed for these tasks. 10-second window so the user has time to
     * read the names and decide whether to manually re-schedule them.
     */
    private renderCascadeConflictToast(conflicts: any[]): void {
        const count = conflicts.length;
        // Show up to 3 task titles inline; if more, append "and N more".
        const previewTitles = conflicts.slice(0, 3).map(c => `"${c.title || c.Title || 'task'}"`);
        let listText = previewTitles.join(', ');
        if (count > 3) {
            listText += ` and ${count - 3} more`;
        }
        const message = `${count} task${count === 1 ? "" : "s"} couldn't be shifted because ${count === 1 ? "it's" : "they're"} in progress or completed: ${listText}.`;

        const options: any = cloneDeep(this.toastrOptions);
        options.timeOut = 10000;
        // extendedTimeOut must be > 0 — with 0, hovering the toast cleared the hide timer and it was
        // never re-armed, so the conflict notice stuck on screen forever (the reported bug). This toast
        // has no Undo button, so also allow tap-to-dismiss as an explicit way to close it.
        options.extendedTimeOut = 5000;
        options.tapToDismiss = true;
        // MUST be true: the app's CustomToastComponent only renders the message div when
        // enableHtml is set (`*ngIf="message && options.enableHtml"`). With it false the
        // conflict toast showed as an empty box (the reported "blank toast"). The message
        // is plain text built from task titles, so rendering it via innerHTML is safe and
        // matches the shift toast above.
        options.enableHtml = true;

        // `warning` matches ngx-toastr's amber/yellow severity — visually distinct
        // from the blue info toast for cascade shifts so the user can tell at a
        // glance "this is a problem to look at" vs. "this is just informational".
        this.toastr.warning(message, null, options);
    }

    /**
     * Restore the dates of cascade-shifted tasks by calling the BE's UndoCascadeShift
     * endpoint. The payload carries each task's old/new dates so the BE can roll back.
     */
    private undoCascadeShift(affectedTasks: any[]): void {
        if (!affectedTasks || affectedTasks.length === 0) return;
        this._taskService.undoCascadeShift(affectedTasks as any).subscribe((result) => {
            if (result && result.isError) {
                this.toastr.error(result.errorMessage || 'Could not undo the cascade shift.', null, this.toastrOptions);
                return;
            }
            this.toastr.success('Cascade shift undone.', null, this.toastrOptions);
            // The BE re-fires getUpdatedTaskCard per restored task, so all open
            // surfaces sync automatically — no manual refresh needed here.
        });
    }



    /**
     * Show a "New version ready" banner in the sidebar after each deployment.
     * Detection only — we never reload on our own. The user chooses:
     *   - Refresh  -> activateUpdate() + reload (load the new version now)
     *   - Later/X  -> dismiss the banner, keep working (new version is picked
     *                 up on their next manual reload).
     */
    private _setupAppUpdateNotifications(): void {
        if (!this.swUpdate.isEnabled) {
            return;
        }

        // Surface the banner once a new version has finished downloading.
        this.swUpdate.versionUpdates.pipe(
            filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
            takeUntil(this.appUpdate$)
        ).subscribe(() => {
            this.ngZone.run(() => this.updateAvailable = true);
        });

        // Poll for new deployments during long-lived sessions (the SW also
        // checks on each app load). 30 min keeps the notice timely without noise.
        interval(30 * 60 * 1000).pipe(takeUntil(this.appUpdate$)).subscribe(() => {
            this.swUpdate.checkForUpdate().catch(() => { /* offline / transient — retry next tick */ });
        });
    }

    /** "Refresh" button — activate the new version and reload into it. */
    refreshForUpdate(): void {
        this.swUpdate.activateUpdate()
            .then(() => document.location.reload())
            .catch(() => document.location.reload());
    }

    /** "Later" / dismiss — hide the banner; the update applies on next manual reload. */
    dismissUpdate(): void {
        this.updateAvailable = false;
    }

    ngOnDestroy() {
        this.clearFavTipTimer();
        if (this.dragulaService.find(this.FAV_DRAGULA)) {
            this.dragulaService.destroy(this.FAV_DRAGULA);
        }
        // destroy all the subscriptions at once
        this.subscription.unsubscribe();
        this.appUpdate$.next();
        this.appUpdate$.complete();
        this.taskUpdates$.next();
        this.taskUpdates$.complete();
        this.pinnedProjectInfo$.next();
        this.pinnedProjectInfo$.complete();
        this.cascadeApplied$.next();
        this.cascadeApplied$.complete();
        this.labelUpdates$.next();
        this.labelUpdates$.complete();
        this.calendarNav$.next();
        this.calendarNav$.complete();
        this.destroy$.next();
        this.destroy$.complete();
        this.subs.unsubscribe();
        // this.dataService.openHelpModal.observers = [];
        this.dataService.showBanner.observers = [];
        this.dataService.closeOtherSidebar.observers = [];
        this.dataService.removeRightSidebarClassIfNotNeeded.observers = [];
        this.dataService.closeProfile.observers = [];
        this.dataService.openUserGuideModal.observers = [];
        // openAddDocModal is now owned by MainLayoutComponent (takeUntil) — no manual reset here.
    }

    private handleContributorToast(ev: any): void {
        let msg: string = '';
        let actorName: string = '';
        let taskTitle: string = '';

        if (!ev || !ev.kind) {
            return;
        }
        actorName = ev.actorUserName || 'Someone';
        taskTitle = ev.taskTitle || ev.taskName || 'a task';

        switch (ev.kind) {
            case 'allContributorStepsComplete':
                // Toast to assignee: their task is ready to close
                if (ev.targetUserId && ev.targetUserId !== this.appSession.userId) {
                    return;
                }
                msg = `All contributor steps are complete on "${taskTitle}". Ready to close.`;
                this.toastr.info(msg);
                break;
            case 'taskMarkedCompleteByOther':
                msg = `${actorName} marked "${taskTitle}" complete.`;
                this.toastr.info(msg);
                break;
            case 'contributorStepForceClosed':
                if (ev.targetUserId && ev.targetUserId !== this.appSession.userId) {
                    return;
                }
                msg = `${actorName} marked "${taskTitle}" complete. Your step was closed.`;
                this.toastr.info(msg);
                break;
            case 'stepAssignedToContributor':
            case 'contributorAdded':
            case 'contributorRemoved':
                // Red dot only — no toast. The dot is handled by the per-task panel open flow.
                break;
            default:
                break;
        }
    }
}
